import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Gift, ExternalLink, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface RewardInfoProps {
  variant?: 'intro' | 'completion';
  annotationCount?: number;
  className?: string;
}

export function RewardInfo({ variant = 'intro', annotationCount = 0, className }: RewardInfoProps) {
  const isEligible = annotationCount >= 2;
  const discordLink = 'https://discord.gg/fvfjuRc2Tk';

  if (variant === 'intro') {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-primary" />
            Earn H100 GPU Credits for Your Annotations!
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">$20 in Tensorpool Credits</p>
                <p className="text-sm text-muted-foreground">
                  Complete 2 song annotations and receive $20 in credits—equivalent to 20 hours of H100 GPU time.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">$10 Per Annotation</p>
                <p className="text-sm text-muted-foreground">
                  You'll earn $10 for each annotation you complete.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Listen Requirement</p>
                <p className="text-sm text-muted-foreground">
                  Please ensure you listen to at least 75% of each song before submitting your annotations.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm font-semibold mb-2">How to Claim Your Reward:</p>
            <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Complete your 2 annotations</li>
              <li>Join the Tensorpool Discord server</li>
              <li>Provide your email address in the LAION credit channel</li>
              <li>Your credits will be transferred to your Tensorpool account</li>
            </ol>
          </div>

          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={() => window.open(discordLink, '_blank')}
          >
            <ExternalLink className="h-4 w-4" />
            Visit Tensorpool Discord
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Completion variant
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-primary" />
          {isEligible ? 'Claim Your Reward!' : 'Keep Going!'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isEligible ? (
          <>
            <div className="p-4 bg-green-50 dark:bg-green-950/50 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <p className="font-semibold text-green-800 dark:text-green-200">
                  Congratulations! You've completed 2 annotations!
                </p>
              </div>
              <p className="text-sm text-green-700 dark:text-green-300">
                You're now eligible for $20 in Tensorpool credits (20 hours of H100 GPU time).
              </p>
            </div>

            <div className="space-y-3">
              <p className="font-semibold">Next Steps to Claim Your Reward:</p>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                <li>Join the Tensorpool Discord server using the link below</li>
                <li>Navigate to the LAION credit channel</li>
                <li>Provide your email address: <span className="font-mono bg-muted px-1 rounded">your-email@example.com</span></li>
                <li>Your credits will be transferred to your Tensorpool account</li>
              </ol>

              <Button
                className="w-full gap-2 mt-4"
                onClick={() => window.open(discordLink, '_blank')}
              >
                <ExternalLink className="h-4 w-4" />
                Join Tensorpool Discord to Claim
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="p-4 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm">
                You've completed <span className="font-bold">{annotationCount}</span> of 2 annotations.
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Complete {2 - annotationCount} more to earn your $20 Tensorpool credit!
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
